import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ROLE_LABELS } from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { EmptyState } from "../components/EmptyState";
import { ExecutiveDashboard } from "./ExecutiveDashboard";

/**
 * PRODUCT_FLOW.md Stage 1 §4 — the guided setup checklist IS the dashboard's
 * empty state for a new Brand: "a new Brand's dashboard should show this
 * checklist front and center, not a chart full of zeroes."
 *
 * There are deliberately NO KPI cards here. The KPI header is FR-DASH-01, which
 * belongs to Phase 8 (DECISIONS.md SCOPE-002) — rendering zeroed cards now would
 * be the exact anti-pattern PRODUCT_FLOW.md warns about.
 */
const SETUP_STEPS = [
  {
    title: "Add your first Narrative Pillar",
    note: "A strategic topic to track — ESG & Net-Zero, Executive Leadership, Financial Stability.",
    phase: null,
  },
  {
    title: "Upload Ground Truth documents",
    note: "Your official press releases, ESG reports, and position statements.",
    phase: null,
  },
  {
    title: "Approve extracted claims",
    note: "Review the atomic claims pulled from those documents before they become the scored baseline.",
    phase: null,
  },
  {
    title: "Approve your first prompts",
    note: "Stakeholder questions a journalist, investor, or regulator would actually ask.",
    phase: "Phase 2",
  },
  {
    title: "Run your first capture",
    note: "Ask all five engines what they say about you, then measure the drift.",
    phase: "Phase 3",
  },
] as const;

export function DashboardPage() {
  const { user, activeBrand } = useAuth();
  const brandId = activeBrand?.brandId ?? null;
  const [pillarCount, setPillarCount] = useState<number | null>(null);

  useEffect(() => {
    if (!brandId) return;
    setPillarCount(null);
    void api<{ items: unknown[] }>(`/brands/${brandId}/pillars`)
      .then((d) => setPillarCount(d.items.length))
      .catch(() => setPillarCount(0));
  }, [brandId]);

  // No Brand yet — the first thing an Organization Admin needs to do.
  if (!activeBrand) {
    const canCreate = (user?.organizationGrants.length ?? 0) > 0;
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">Welcome to Echo</h1>
          <p className="page__subtitle">Let's get your first Brand set up.</p>
        </header>
        <div className="card">
          <EmptyState
            icon="brand"
            title="Create your first Brand"
            description={
              canCreate
                ? "A Brand is one company whose AI-mediated narrative you track. An agency creates one per client."
                : "You don't have access to any Brands yet. Ask your Organization Admin to invite you to one."
            }
            action={
              canCreate ? (
                <Link className="btn btn--primary" to="/settings/brands/new">
                  Create a Brand
                </Link>
              ) : undefined
            }
          />
        </div>
      </>
    );
  }

  // PRODUCT_FLOW.md Stage 1 §4 — the guided checklist IS the empty state, and
  // it is replaced by the real dashboard the moment there is something to
  // measure. A configured Brand should never be sent back to a setup list.
  if (pillarCount === null) {
    return (
      <div className="skeleton-stack">
        <div className="skeleton" style={{ height: 32, width: "40%" }} />
        <div className="skeleton" style={{ height: 120 }} />
      </div>
    );
  }

  if (pillarCount > 0) return <ExecutiveDashboard />;

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">{activeBrand.brandName}</h1>
        <p className="page__subtitle">
          You're signed in as {ROLE_LABELS[activeBrand.role]}
          {activeBrand.inherited && " (inherited from your Organization role)"}.
        </p>
      </header>

      <div className="card">
        <EmptyState
          icon="sparkle"
          title="Add your first Narrative Pillar"
          description={
            <>
              Nothing is being tracked for this Brand yet. Narrative Pillars are
              the strategic topics Echo measures drift against — start with one,
              and the rest of the setup follows from it.
            </>
          }
          action={
            // Live as of Phase 1 — this was the inert CTA in Phase 0.
            <Link className="btn btn--primary" to="/ground-truth">
              Add Narrative Pillar
            </Link>
          }
        />

        <ol className="checklist">
          {SETUP_STEPS.map((step, index) => (
            <li className="checklist__item" key={step.title}>
              <span className="checklist__marker" aria-hidden="true">
                {index + 1}
              </span>
              <span className="checklist__body">
                <span className="checklist__title">{step.title}</span>
                <br />
                <span className="checklist__note">{step.note}</span>
              </span>
              {step.phase ? (
                <span className="badge badge--neutral">{step.phase}</span>
              ) : (
                <Link className="badge badge--accent" to="/ground-truth">
                  Start
                </Link>
              )}
            </li>
          ))}
        </ol>

        <p
          style={{
            marginTop: "var(--space-4)",
            color: "var(--color-text-secondary)",
            fontSize: "var(--font-caption-size)",
          }}
        >
          Steps without a phase badge are live now. The rest light up as their
          phase lands — see docs/PHASED_IMPLEMENTATION_PLAN.md.
        </p>
      </div>
    </>
  );
}
